"use client";

import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const categories = [{
  id: "all",
  label: "All"
}, {
  id: "tips",
  label: "Naming Tips"
}, {
  id: "trends",
  label: "Trends"
}, {
  id: "origins",
  label: "Origins"
}, {
  id: "lists",
  label: "Top Lists"
}];
function formatDate(dateStr?: string): string {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      month: "short",
      year: "numeric"
    });
  } catch {
    return "";
  }
}
function estimateReadTime(content?: string, excerpt?: string): string {
  const text = (content || excerpt || "").replace(/<[^>]+>/g, "");
  const words = text.split(/\s+/).filter(Boolean).length;
  const minutes = Math.max(1, Math.round(words / 200));
  return `${minutes} min`;
}
function ArchiveGrid() {
  const {
    posts,
    loading,
    error,
    refetch,
    currentPage,
    totalPages,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    setPage
  } = usePosts() as ReturnType<typeof usePosts> & {
    currentPage: number;
  };
  if (loading) {
    return <div className="grid grid-cols-1 gap-x-8 gap-y-14 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(9)].map((_, i) => <div key={i} data-index={i} className="rounded-lg border border-border bg-card overflow-hidden shadow-md">
            <div className="aspect-[4/3] w-full bg-muted/40 animate-pulse" />
            <div className="p-6 space-y-3">
              <div className="h-3 w-2/5 bg-muted/50 animate-pulse rounded-sm" />
              <div className="h-5 w-4/5 bg-muted/60 animate-pulse rounded-sm" />
              <div className="h-3 w-full bg-muted/40 animate-pulse rounded-sm" />
              <div className="h-3 w-3/4 bg-muted/40 animate-pulse rounded-sm" />
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center gap-4 py-16 text-center">
        <p className="text-base text-muted-foreground">
          We couldn{"'"}t load the naming articles right now.
        </p>
        <Button variant="outline" onClick={() => refetch()} className="border-foreground/30 text-foreground hover:bg-foreground/5">
          Try again
        </Button>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="py-16 text-center">
        <p className="font-default text-2xl text-foreground/80">
          No articles published <em className="italic">yet</em>.
        </p>
        <p className="mt-3 text-sm text-muted-foreground">
          New naming guides and inspiration will appear here soon.
        </p>
      </div>;
  }
  const pageNumbers: number[] = [];
  const maxVisible = 4;
  let start = Math.max(1, currentPage - 1);
  let end = Math.min(totalPages, start + maxVisible - 1);
  if (end - start + 1 < maxVisible) {
    start = Math.max(1, end - maxVisible + 1);
  }
  for (let p = start; p <= end; p++) pageNumbers.push(p);
  return <>
      <div className="grid grid-cols-1 gap-x-8 gap-y-14 md:grid-cols-2 lg:grid-cols-3">
        {posts.map((post, i) => {
        const dateLabel = formatDate(post.date);
        const readTime = estimateReadTime(post.content, post.excerpt);
        const categoryLabel = post.categories && post.categories.length > 0 ? post.categories[0].name : "Guide";
        const excerptText = (post.excerpt || "").replace(/<[^>]+>/g, "").trim();
        return <article key={post.id} data-index={i} className="group rounded-lg border border-border bg-card overflow-hidden shadow-md transition-colors duration-300 ease-out hover:border-primary/50">
              <Link to={post.link || "#"} className="block">
                <div className="aspect-[4/3] w-full overflow-hidden bg-muted/40">
                  {post.featuredImage ? <img src={post.featuredImage} alt={post.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]" /> : <div className="h-full w-full bg-gradient-to-br from-primary/10 to-secondary/20" />}
                </div>

                <div className="p-6">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[0.8125rem] text-muted-foreground">
                    <span>{categoryLabel}</span>
                    {dateLabel && <>
                        <span aria-hidden="true" className="inline-block h-[3px] w-[3px] rounded-sm bg-muted-foreground/30" />
                        <span>{dateLabel}</span>
                      </>}
                    <span aria-hidden="true" className="inline-block h-[3px] w-[3px] rounded-sm bg-muted-foreground/30" />
                    <span>{readTime}</span>
                  </div>

                  <h3 className="mt-3 font-default text-[1.1875rem] leading-[1.3] font-semibold text-foreground line-clamp-2 transition-transform duration-300 ease-out group-hover:translate-x-1">
                    {post.title}
                  </h3>

                  <div className="mt-2 h-[1.5px] w-0 bg-primary transition-[width] duration-300 ease-out group-hover:w-full" />

                  {excerptText && <p className="mt-3 text-[0.9375rem] leading-[1.55] text-foreground/80 line-clamp-2">
                      {excerptText}
                    </p>}
                </div>
              </Link>
            </article>;
      })}
      </div>

      {hasNext && <div className="mt-20 flex justify-center">
          <Button variant="default" size="lg" onClick={() => nextPage()} className="min-w-[200px]">
            Load More
          </Button>
        </div>}
    </>;
}
export default function Blog() {
  const [activeCategory, setActiveCategory] = useState<string>("all");
  const wp_query_params: Record<string, unknown> = {
    post_type: "post",
    per_page: 15,
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="15027" id="blog-archive" className="bg-background text-foreground py-28 md:py-40">
      <div className="mx-auto max-w-[1320px] px-5 md:px-10 lg:px-14">
        <div className="flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <span className="block font-default text-[0.8125rem] font-bold uppercase tracking-[0.14em] text-primary">
              Resources
            </span>
            <h2 className="mt-4 font-default font-bold text-foreground text-[clamp(2rem,4.2vw,4rem)] leading-[1.08] tracking-tight">
              Naming{" "}
              <span className="relative italic text-primary">
                Insights
                <span aria-hidden="true" className="absolute left-0 right-0 -bottom-[0.18em] h-[1.5px] bg-primary" />
              </span>
              .
            </h2>
          </div>

          <div className="lg:max-w-[55%]">
            <div className="-mx-5 overflow-x-auto px-5 lg:mx-0 lg:overflow-visible lg:px-0">
              <ul className="flex min-w-max items-center gap-5 lg:min-w-0 lg:flex-wrap lg:justify-end lg:gap-6" role="list">
                {categories.map((cat, i) => {
                const active = activeCategory === cat.id;
                return <li key={cat.id} data-index={i} className="snap-start">
                      <Button variant="ghost" onClick={() => setActiveCategory(cat.id)} className={`h-auto p-0 font-default text-sm transition-colors duration-200 relative ${active ? "text-primary font-semibold" : "text-foreground/60 hover:text-primary hover:bg-transparent"}`}>
                        <span className="relative inline-block">
                          {cat.label}
                          <span aria-hidden="true" className={`absolute left-0 right-0 -bottom-1.5 h-[1.5px] bg-primary transition-opacity duration-200 ${active ? "opacity-100" : "opacity-0"}`} />
                        </span>
                      </Button>
                    </li>;
              })}
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-16">
          <PostsProvider wp_query={wp_query}>
            <ArchiveGrid />
          </PostsProvider>
        </div>
      </div>
    </section>;
}